import { useNavigate, useLocation, Link } from "react-router-dom";
import type { ReactNode } from "react";

function LegalLink({
  section,
  children,
  className,
}: {
  section: string;
  children: ReactNode;
  className: string;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pathname === "/legal") {
      const el = document.getElementById(section);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate(`/legal#${section}`);
    }
  };

  return (
    <a href={`/legal#${section}`} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#182940] text-white/70">
      <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/2cfbf5a81e0a4ae58e7b560ce5d855ea.png"
                alt="Dental Safety Abroad"
                className="h-20 w-auto"
              />
            </Link>
            <p className="text-sm leading-relaxed text-white/60">
              Independent guidance for safer dental care abroad. No clinic affiliations. No hidden agendas.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Resources</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/blog" className="text-sm text-white/60 hover:text-primary-400 transition-colors">
                  Blog & Insights
                </Link>
              </li>
              <li>
                <Link to="/quiz" className="text-sm text-white/60 hover:text-primary-400 transition-colors">
                  Destination Quiz
                </Link>
              </li>
              <li>
                <Link to="/checklist" className="text-sm text-white/60 hover:text-primary-400 transition-colors">
                  Red Flags Guide
                </Link>
              </li>
              <li>
                <Link to="/country-fit" className="text-sm text-white/60 hover:text-primary-400 transition-colors">
                  Country Comparison
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm text-white/60 hover:text-primary-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-white/60 hover:text-primary-400 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <LegalLink
                  section="disclaimer"
                  className="text-sm text-white/60 hover:text-primary-400 transition-colors"
                >
                  Disclaimer
                </LegalLink>
              </li>
              <li>
                <LegalLink
                  section="privacy"
                  className="text-sm text-white/60 hover:text-primary-400 transition-colors"
                >
                  Privacy Policy
                </LegalLink>
              </li>
              <li>
                <LegalLink
                  section="affiliate"
                  className="text-sm text-white/60 hover:text-primary-400 transition-colors"
                >
                  Affiliate Disclosure
                </LegalLink>
              </li>
              <li>
                <LegalLink
                  section="cookies"
                  className="text-sm text-white/60 hover:text-primary-400 transition-colors"
                >
                  Cookie Policy
                </LegalLink>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-white/50">
            &copy; {new Date().getFullYear()} Dental Safety Abroad. All rights reserved.
          </span>
          <div className="flex items-center gap-3 text-sm text-white/50">
            <LegalLink section="disclaimer" className="hover:text-primary-400 transition-colors">Disclaimer</LegalLink>
            <span>&middot;</span>
            <LegalLink section="privacy" className="hover:text-primary-400 transition-colors">Privacy</LegalLink>
            <span>&middot;</span>
            <LegalLink section="affiliate" className="hover:text-primary-400 transition-colors">Affiliates</LegalLink>
            <span>&middot;</span>
            <LegalLink section="cookies" className="hover:text-primary-400 transition-colors">Cookies</LegalLink>
          </div>
        </div>
      </div>
    </footer>
  );
}